import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const UnitForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [sites, setSites] = useState([]);
    const [wings, setWings] = useState([]);
    const [floors, setFloors] = useState([]);

    const [formData, setFormData] = useState({
        siteId: null,
        wingId: null,
        floorId: null,
        unitName: '',
        UnitNumber: '',
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editId = location.state?.id;

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Sites
                const siteRes = await apiServices({ method: 'GET', url: '/SiteDetailsMaster/getAllSiteDetails' });
                if (siteRes.success) {
                    let siteData = siteRes.data?.Data || [];
                    setSites(siteData.map(s => ({
                        label: s.SiteName,
                        value: s.Id
                    })));
                }

                // Fetch Wings
                const wingRes = await apiServices({ method: 'GET', url: '/WingMaster/GetAllWing' });
                if (wingRes.success) {
                    const wingData = wingRes.data?.WingMasterData || [];
                    setWings(wingData);
                }

                // Fetch Floors
                const floorRes = await apiServices({ method: 'GET', url: '/FloorMaster/GetAllFloor' });
                if (floorRes.success) {
                    const floorData = floorRes.data?.FloorMasterData || [];
                    setFloors(floorData);
                }

                // If edit mode, fetch unit details
                if (isEdit) {
                    setIsFetching(true);
                    const unitRes = await apiServices({ method: 'GET', url: `/UnitMaster/GetUnitMasterById/${editId}` });

                    if (unitRes.success) {
                        let apiData = unitRes?.data?.data[0];

                        setFormData({
                            siteId: apiData.SiteId,
                            wingId: apiData.WingId,
                            floorId: apiData.FloorId,
                            unitName: apiData.UnitName || '',
                            UnitNumber: apiData.UnitNumber || '',
                            isActive: apiData.IsActive || 'Active'
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            } finally {
                setIsFetching(false);
            }
        };

        fetchMasterData();
    }, [isEdit, editId]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const newData = { ...prev, [key]: option ? option.value : null };
            if (key === 'siteId') {
                newData.wingId = null;
                newData.floorId = null;
            } else if (key === 'wingId') {
                newData.floorId = null;
            }
            return newData;
        });
    };

    const validateForm = () => {
        if (!formData.siteId) return toast.warning("Site is required"), false;
        if (!formData.wingId) return toast.warning("Wing is required"), false;
        if (!formData.floorId) return toast.warning("Floor is required"), false;
        if (!formData.unitName || !formData.unitName.trim()) return toast.warning("Unit Name is required"), false;
        if (!formData.UnitNumber || !String(formData.UnitNumber).trim()) return toast.warning("Unit Number is required"), false;
        if (!formData.isActive) return toast.warning("Status is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            siteId: formData.siteId,
            wingId: formData.wingId,
            floorId: formData.floorId,
            unitName: formData.unitName,
            unitNumber: formData.UnitNumber,
            isActive: formData.isActive === 'Active' ? 1 : 0,
            CreatedBy: "sys"

        };

        try {
            const url = isEdit ? `UnitMaster/UpdateExistingUnitMaster/${editId}` : `UnitMaster/AddNewUnitMaster`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success && (response.data?.IsSuccess || response.data?.isSuccess || response.data?.success || response.IsSuccess)) {
                toast.success(response.data?.Message || response.data?.message || `Unit ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentcomm/site-management/unit');
            } else {
                toast.error(response.data?.Message || response.data?.message || response.message || `Failed to ${isEdit ? 'update' : 'create'} unit.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    const filteredWings = wings
        .filter(w => !formData.siteId || (w.SiteId || w.siteId) == formData.siteId)
        .map(w => ({ label: w.WingName || w.wingName, value: w.Id || w.id || w.WingId }));

    const filteredFloors = floors
        .filter(f => !formData.wingId || (f.WingId || f.wingId) == formData.wingId)
        .map(f => ({ label: f.FloorName || f.floorName || f.FloorNumber || f.floorNumber, value: f.Id || f.id || f.FloorId }));

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Unit`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} unit.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <CustomSelect
                    label={<>Site Name <span className="text-red-500">*</span></>}
                    options={sites}
                    value={sites.find(s => s.value === formData.siteId) || null}
                    onChange={(opt) => handleSelectChange('siteId', opt)}
                />

                <CustomSelect
                    label={<>Wing Name <span className="text-red-500">*</span></>}
                    options={filteredWings}
                    value={filteredWings.find(w => w.value === formData.wingId) || null}
                    onChange={(opt) => handleSelectChange('wingId', opt)}
                    disabled={!formData.siteId}
                />

                <CustomSelect
                    label={<>Floor <span className="text-red-500">*</span></>}
                    options={filteredFloors}
                    value={filteredFloors.find(f => f.value === formData.floorId) || null}
                    onChange={(opt) => handleSelectChange('floorId', opt)}
                    disabled={!formData.wingId}
                />

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="unitName">
                        Unit Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="unitName"
                        value={formData.unitName}
                        onChange={handleInputChange}
                        placeholder="e.g., A-101"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="UnitNumber">
                        Unit Number <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="UnitNumber"
                        value={formData.UnitNumber}
                        onChange={handleInputChange}
                        placeholder="e.g., 101"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentcomm/site-management/unit")}
                    className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default UnitForm;
